/**
 * Gesperrte Mitglieder aus jeder Personenauswahl heraushalten (Migration
 * Nr. 148, `supabase/gesperrte_konten.sql`).
 *
 * Die Sperre aus Nr. 138 steht in `auth.users.banned_until`; `profiles` weiß
 * davon nichts. Die Benutzerverwaltung liest den Status über
 * `benutzer_sperrstatus()` (nur Admins) — alle anderen Auswahllisten boten
 * eine gesperrte Person weiter an (Betreiber 12.10.2026: Zuweisung,
 * Buchhaltung). `gesperrte_konten()` liefert die Ids für jeden Internen.
 *
 * Ein Speicher für die ganze Sitzung: geladen beim ersten Gebrauch, erneuert
 * nach `GUELTIG_MS` — solange eine Auswahl offen ist, prüft ein Takt das von
 * selbst (eine Sperre schlägt also auch in einem stehenden Dialog durch) —
 * oder sofort nach einem Sperren/Entsperren (`aktualisiereGesperrteKonten`).
 * Wechselt die Anmeldung (Abmelden, anderes Konto), wird der Stand verworfen;
 * eine Antwort, die noch für die alte Anmeldung unterwegs war, zählt nicht.
 * Fehlt die Funktion oder scheitert das Laden, bleibt die Menge leer — die
 * Auswahl verhält sich dann wie vor Nr. 148, statt jemanden auszublenden, der
 * gar nicht gesperrt ist.
 */
import { supabase } from './supabaseClient';

const GUELTIG_MS = 5 * 60 * 1000;
const TAKT_MS = 60 * 1000;
const LEER: ReadonlySet<string> = new Set();

let menge: ReadonlySet<string> = LEER;
let geladenAm = 0;
let laufend: Promise<void> | null = null;
/** Zählt Anmeldungswechsel; eine Antwort aus einer früheren zählt nicht. */
let sitzung = 0;
/** `undefined` = noch kein Auth-Ereignis gesehen. */
let angemeldetAls: string | null | undefined = undefined;
let authAbo: { unsubscribe: () => void } | null = null;
let takt: ReturnType<typeof setInterval> | null = null;
const hoerer = new Set<() => void>();

const melden = () => { for (const h of hoerer) h(); };

/** `setof uuid` kommt als Array von Strings; alles andere fällt weg. */
export async function ladeGesperrteKonten(): Promise<Set<string> | null> {
    const { data, error } = await supabase.rpc('gesperrte_konten');
    if (error) return null;
    return new Set(((data ?? []) as unknown[]).filter((v): v is string => typeof v === 'string' && v !== ''));
}

const laden = (): Promise<void> => {
    if (laufend) return laufend;
    const nr = sitzung;
    const lauf: Promise<void> = (async () => {
        try {
            const neu = await ladeGesperrteKonten();
            if (nr !== sitzung) return;
            // Bei Fehler den letzten Stand behalten, aber nicht sofort erneut
            // versuchen — sonst fragte jede Auswahl beim Rendern wieder an.
            geladenAm = Date.now();
            if (neu) { menge = neu; melden(); }
        } catch {
            if (nr === sitzung) geladenAm = Date.now();
        } finally {
            if (laufend === lauf) laufend = null;
        }
    })();
    laufend = lauf;
    return lauf;
};

/** Stößt das Laden an, wenn der Stand fehlt oder älter als `GUELTIG_MS` ist. */
export const holeGesperrteKonten = (jetzt: number = Date.now()): void => {
    if (!laufend && jetzt - geladenAm >= GUELTIG_MS) void laden();
};

/** Sofort neu laden — nach Sperren/Entsperren in der Benutzerverwaltung. */
export const aktualisiereGesperrteKonten = (): Promise<void> => {
    geladenAm = 0;
    return laufend ? laufend.then(laden) : laden();
};

/** Anmeldung gewechselt: Stand verwerfen, laufende Antwort entwerten. */
const anmeldungGewechselt = (userId: string | null) => {
    const vorher = angemeldetAls;
    angemeldetAls = userId;
    if (vorher === undefined || vorher === userId) return;
    sitzung++;
    menge = LEER; geladenAm = 0; laufend = null;
    melden();
    // Nicht direkt im Auth-Rückruf an Supabase: supabase-js hält dort die
    // Auth-Sperre, ein rpc darin kann hängen bleiben.
    if (userId && hoerer.size > 0) setTimeout(() => holeGesperrteKonten(), 0);
};

const authBeobachten = () => {
    if (authAbo) return;
    try {
        const r = supabase.auth?.onAuthStateChange?.((_ereignis, session) => {
            anmeldungGewechselt(session?.user?.id ?? null);
        });
        authAbo = r?.data?.subscription ?? null;
    } catch {
        authAbo = null;
    }
};

export const gesperrteKontenStand = (): ReadonlySet<string> => menge;

export const abonniereGesperrteKonten = (h: () => void): (() => void) => {
    hoerer.add(h);
    authBeobachten();
    if (!takt) takt = setInterval(() => holeGesperrteKonten(), TAKT_MS);
    return () => {
        hoerer.delete(h);
        if (hoerer.size === 0 && takt) { clearInterval(takt); takt = null; }
    };
};

/** Nur für Tests: Speicher auf den Anfang zurück. */
export const __setzeGesperrteKontenZurueck = (): void => {
    if (takt) clearInterval(takt);
    authAbo?.unsubscribe();
    menge = LEER; geladenAm = 0; laufend = null; sitzung = 0;
    angemeldetAls = undefined; authAbo = null; takt = null; hoerer.clear();
};

type MitId = { id?: string | null };

/**
 * Personen für eine Auswahlliste, ohne gesperrte Konten.
 *
 * `behalten` sind die Ids, die gerade gewählt sind: eine gesperrte Person,
 * die noch zugewiesen ist, bleibt in der Liste — sonst zeigte das Feld leer
 * oder eine andere Person, und das nächste Speichern ersetzte die Zuweisung
 * still. Abwählen geht, neu wählen nicht.
 */
export function ohneGesperrte<T extends MitId>(
    personen: readonly T[],
    gesperrt: ReadonlySet<string>,
    behalten?: string | null | readonly (string | null | undefined)[],
): T[] {
    if (gesperrt.size === 0) return personen.slice();
    const bleibt = new Set<string>(
        (Array.isArray(behalten) ? behalten : [behalten]).filter((v): v is string => typeof v === 'string' && v !== ''),
    );
    return personen.filter(p => !p.id || !gesperrt.has(p.id) || bleibt.has(p.id));
}

/** Zusatz für die Beschriftung einer noch zugewiesenen, gesperrten Person. */
export const gesperrtZusatz = (id: string | null | undefined, gesperrt: ReadonlySet<string>): string =>
    id && gesperrt.has(id) ? ' (gesperrt)' : '';
